import { Text, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, FormControl, FormLabel, Input, ModalFooter, HStack, Spinner, Select, Textarea, Alert, AlertIcon } from '@chakra-ui/react'
import React from 'react'
import { IProvider } from '@/shared/IProvider';
import { ErrorMessage } from '../components/ErrorMessage';
import { useOperation } from '../components/useOperation';
import { CourseSegmentOptions } from './filters/CourseSegmentOptions';
import { IUserInfo } from '@/shared/IUserInfo';
import { IExpert } from '@/shared/IExpert';
import { UserListControl } from './UserListControl';
import { CourseProviderStorage } from '../services/CourseProviderStorage';
import { CourseExpertStorage } from '../services/CourseExpertStorage';
import { NewProviderDialog } from './NewProviderDialog';
import { ICourse } from '@/shared/ICourse';
import { isSameEmail, makeAcronym } from '@/shared/parse';
import { INVITE_TYPE, PLATFORM_VERSION } from '@/shared/constants';
import { WorkflowService } from '@/shared/WorkflowService';
import { AuthService } from '../services/AuthService';
import { UserInfoService } from '../services/UserInfoService';
import { CourseStorageService } from '../services/CourseStorageService';
import { TelemetryClientService } from '../services/TelemetryClientService';

export const CourseDialog = (props: {
  onClose: () => void;

  course: ICourse;
  onSave: (data: ICourse) => Promise<void>;

  // Dialog configuration
  title: string;
  description?: string;
  saveLabel: string;
  savingLabel: string;
}) => {
  // Internal course state management
  const [course, setCourse] = React.useState<ICourse>(props.course);

  // Sync with initial course data when dialog opens or props change
  React.useEffect(() => {
    setCourse(props.course);
  }, [props.course]);

  // Provider management state (only used when enableProviderManagement is true)
  const [providers, setProviders] = React.useState<IProvider[]>([]);
  const [loadErrorMessage, setLoadErrorMessage] = React.useState<any>();
  const [isNewProviderDialogOpen, setIsNewProviderDialogOpen] = React.useState(false);

  // Experts state for user assignment
  const [experts, setExperts] = React.useState<IExpert[]>([]);

  // Duplicate title warning state
  const [duplicateCourse, setDuplicateCourse] = React.useState<ICourse | null>(null);

  // Load providers when dialog opens
  React.useEffect(() => {
    loadProviders();
    loadExperts();
    loadStaff();
  }, []);

  const loadProviders = async () => {
    setLoadErrorMessage(undefined);
    try {
      const loaded = await CourseProviderStorage.getProviders();
      setProviders(loaded);
    } catch (err) {
      setLoadErrorMessage(err);
    }
  };

  const [staff, setStaff] = React.useState<IUserInfo[]>([]);
  const loadStaff = async () => {
    try {
      const loaded = await CourseExpertStorage.getStaff();
      setStaff(loaded);
      if (!course.id) {
        // If course is new, set the first expert as the default provider user
        const defaultStaffUser = loaded.find(e => isSameEmail(e.email, AuthService.account?.username));
        if (defaultStaffUser) {
          setCourse(prev => ({ ...prev, CourseStaffUsers: [defaultStaffUser] }));
        }
      }
    } catch (err) {
      console.error('Failed to load staff users:', err);
    }
  }

  const loadExperts = async () => {
    try {
      const loaded = await CourseExpertStorage.getExperts();
      setExperts(loaded);
    } catch (err) {
      console.error('Failed to load experts:', err);
    }
  };

  // Check for duplicate course title
  React.useEffect(() => {
    const checkDuplicateTitle = async () => {
      if (!course.CourseTitle || course.CourseTitle.trim().length === 0) {
        setDuplicateCourse(null);
        return;
      }

      try {
        const { courses } = await CourseStorageService.getCourses();
        const normalizedTitle = course.CourseTitle.trim().toLowerCase();
        
        // Find duplicate course (exclude current course if editing)
        const duplicate = courses.find(c => 
          c.id !== course.id && 
          c.CourseTitle.trim().toLowerCase() === normalizedTitle
        );
        
        setDuplicateCourse(duplicate || null);
      } catch (err) {
        TelemetryClientService.error('Failed to check for duplicate course title:', err);
      }
    };

    checkDuplicateTitle();
  }, [course.CourseTitle, course.id]);

  const onAddNewProvider = async (created: IProvider) => {
    setProviders(old => [...old, created]);
    setCourse(prev => ({ ...prev, providerId: created.id }));
  }

  const [onSave, saveErrorMessage, isProcessing] = useOperation(async () => {
    await props.onSave(course);
    props.onClose();
  }, [course]);

  const onProviderChange = (providerId: string) => {
    const selectedProvider = providers.find(p => p.id === providerId);
    setCourse(prev => ({
      ...prev,
      providerId,
      provider: selectedProvider,
      CourseProviderUsers: prev.CourseProviderUsers?.filter(u => selectedProvider?.ProviderUsers?.some(pu => pu.id === u.id)) || []
    }));
  }

  const provider = providers.find(p => p.id === course.providerId);

  const initialRef = React.useRef(null)

  const isUserSupportEnabled = course.coursePlatformVersion === PLATFORM_VERSION.V2;

  // Default validation if not provided
  const isDisabled = !course.CourseTitle || !course.CourseSegment || !course.providerId || isProcessing;

  const acronymPlaceholder = makeAcronym(course.CourseTitle || '');

  const defaultProviderUser = WorkflowService.getdefaultProviderUser(provider);

  const onAddProviderUser = async (user: IUserInfo, invite: boolean) => {
    if (provider && !provider.ProviderUsers?.find(u => isSameEmail(u.email, user.email))) {
      const { ensured } = await UserInfoService.ensureUser(user, invite ? INVITE_TYPE.PROVIDER : INVITE_TYPE.NONE);
      const updated = await CourseProviderStorage.patchProvider({ ...provider, ProviderUsers: [...(provider.ProviderUsers || []), ensured] }, invite);
      setProviders(prev => prev.map(p => p.id === updated.id ? updated : p));
      return ensured;
    } else {
      return user;
    }
  }

  const onAddStaffUser = async (user: IUserInfo, invite: boolean) => {
    if (!experts.find(e => isSameEmail(e.email, user.email))) {
      const { ensured } = await UserInfoService.ensureUser(user, invite ? INVITE_TYPE.PROVIDER : INVITE_TYPE.NONE);
      const newExpert: IExpert = { ...ensured, role: 'staff' };
      const created = await CourseExpertStorage.createExpert(newExpert, invite);
      setExperts(prev => [...prev, created]);
      return ensured;
    } else {
      return user;
    }
  }

  return (
    <Modal
      initialFocusRef={initialRef}
      finalFocusRef={initialRef}
      size={{ base: 'full', md: 'lg', lg: 'xl' }}
      isOpen
      onClose={props.onClose}
      preserveScrollBarGap
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent maxH="90vh" my={{ base: 0, md: 8 }}>
        <ModalHeader>{props.title}</ModalHeader>
        <ModalCloseButton />        <ModalBody pb={6} overflowY="auto">
          {loadErrorMessage && <ErrorMessage error={loadErrorMessage} />}
          <ErrorMessage error={saveErrorMessage} />

          {props.description && (
            <>
              <Text mb={1}>{props.description}</Text>
              {props.description.includes('new course') && (
                <Text fontSize='small' color='fg.subtle'>
                  To create a repeated run of an existing course, please use the "Submit Repeated Course" from the existing course's context menu instead.
                </Text>
              )}
            </>
          )}

          <FormControl isRequired key='name' mt='4'>
            <FormLabel size='md'>Course Name</FormLabel>
            <Input
              ref={initialRef}
              placeholder={course.CourseTitle ? undefined : 'The Power of Mobility'}
              value={course.CourseTitle || ''}
              onChange={e => setCourse(prev => ({ ...prev, CourseTitle: e.target.value }))}
            />
            <Text mt='1' fontSize='small' color='fg.subtle'>The course provider will be able to modify it when submitting the survey</Text>
            
            {duplicateCourse && (
              <Alert status='warning' mt='2' borderRadius='md'>
                <AlertIcon />
                <Text fontSize='sm'>
                  A course with this title already exists: <strong>{duplicateCourse.folderName}</strong>. 
                  Please consider using a different title to avoid confusion, unless it is intentional (e.g., a repeated course).
                </Text>
              </Alert>
            )}
          </FormControl>

          <FormControl key='acronym' mt='4'>
            <FormLabel>Course Identifier</FormLabel>
            <Input
              value={course.CourseIdentifier || ''}
              placeholder={acronymPlaceholder || "TPM"}
              onChange={e => setCourse(prev => ({ ...prev, CourseIdentifier: e.target.value }))}
            />
            <Text mt='1' fontSize='small' color='fg.subtle'>
              An acronym to identify the course on EIT UM. We will try to generate it from course name
            </Text>
          </FormControl>

          <FormControl isRequired key='segment' mt='4'>
            <FormLabel>Course Segment</FormLabel>
            <Select isRequired placeholder='Please select...' value={course.CourseSegment} onChange={e => setCourse(prev => ({ ...prev, CourseSegment: e.target.value }))}>
              {CourseSegmentOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select><Text mt='1' fontSize='small' color='fg.subtle'>
              Please select if this is an academy course, a competence hub course, or other course.
            </Text>
          </FormControl>

          <HStack spacing={4} mt='4' alignItems="flex-end">
            <FormControl isRequired key='provider' mt='4'>
              <FormLabel>Course Provider</FormLabel>
              <Select isRequired placeholder='Please select...' value={course.providerId} onChange={e => onProviderChange(e.target.value)}>
                {providers.map((provider: IProvider) => (
                  <option key={provider.id} value={provider.id}>
                    {`${provider.ProviderName} (${provider.ProviderIdentifier})`}
                  </option>
                ))}
              </Select>
              <Text mt='1' fontSize='small' color='fg.subtle'>Please select a provider for this course.</Text>
            </FormControl>
            <Button onClick={() => setIsNewProviderDialogOpen(true)} variant='outline' mb="6">New...</Button>
          </HStack>

          {isUserSupportEnabled &&
            <FormControl key='CourseUsers' mt='4'>
              <FormLabel>Responsible Provider Users</FormLabel>
              <UserListControl
                availableUsers={provider?.ProviderUsers || []}
                enableAdd={true}
                onAddUser={onAddProviderUser}
                placeholder={defaultProviderUser && `${defaultProviderUser.name} (provider's default)`}
                users={course.CourseProviderUsers ?? []}
                onSetUsers={(users: IUserInfo[]) => setCourse({ ...course, CourseProviderUsers: users })}
                title='Add Course User'
                description='This user will be able to access and manage this course.'
              />
            </FormControl>}

          {isUserSupportEnabled &&
            <FormControl key='StaffUsers' mt='4'>
              <FormLabel>Responsible Staff Users</FormLabel>
              <UserListControl
                availableUsers={staff}
                enableAdd={false}
                onAddUser={onAddStaffUser}
                users={course.CourseStaffUsers ?? []}
                onSetUsers={(users: IUserInfo[]) => setCourse({ ...course, CourseStaffUsers: users })}
                title='Add Staff User'
                description='This user will be notified about course updates.'
              />
            </FormControl>}

          {AuthService.isStaff &&
            <FormControl key='CourseNotice' mt='4'>
              <FormLabel>Staff Notice</FormLabel>
              <Textarea
                placeholder='Internal notes for staff users only...'
                value={course.CourseNotice || ''}
                onChange={e => setCourse(prev => ({ ...prev, CourseNotice: e.target.value }))}
                rows={3}
              />
              <Text mt='1' fontSize='small' color='fg.subtle'>
                This notice is only visible to staff users. It will appear in the course table, on hover tooltips, and on the course page.
              </Text>
            </FormControl>}

        </ModalBody>

        <ModalFooter>
          <Button variant='solid' isDisabled={isDisabled} mr={4} onClick={onSave}>
            <HStack spacing={2}>{isProcessing && <Spinner />}<Text>{isProcessing ? props.savingLabel : props.saveLabel}</Text></HStack>
          </Button>
          <Button variant='outline' onClick={props.onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>

      {isNewProviderDialogOpen && <NewProviderDialog
        onClose={() => setIsNewProviderDialogOpen(false)}
        onSave={onAddNewProvider}
      />}
    </Modal>
  )
}
